import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { MessageDescriptor, useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { StreamingChartMetric, StreamingChartSplit } from '../buildStreamingChartData';
import { streamingTotalLabels } from '../streamingTotalsMessages';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

const SPLIT_LABELS: Record<StreamingChartSplit, MessageDescriptor> = {
  total: messages.splitTotal,
  source: messages.splitSource,
  store: messages.splitStore,
  country: messages.splitCountry,
};

const SPLITS: readonly StreamingChartSplit[] = ['total', 'source', 'store', 'country'];

interface StreamingChartControlsProps {
  metrics: StreamingChartMetric[];
  metric: StreamingChartMetric;
  split: StreamingChartSplit;
  /** False: the split Store is off and shows a hint (no connected source reports stores). */
  canSplitByStore: boolean;
  /** False: the split Country is off and shows a hint (no connected source reports countries). */
  canSplitByCountry: boolean;
  onMetric: (metric: StreamingChartMetric) => void;
  onSplit: (split: StreamingChartSplit) => void;
}

/** The number select and "Split by" of the chart (design b1). */
const StreamingChartControlsComponent: React.FC<StreamingChartControlsProps> = ({
  metrics,
  metric,
  split,
  canSplitByStore,
  canSplitByCountry,
  onMetric,
  onSplit,
}) => {
  const intl = useIntl();
  const [hint, setHint] = useState(false);

  const pickMetric = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => onMetric(event.target.value as StreamingChartMetric),
    [onMetric]
  );

  const buttons = useMemo(
    () =>
      SPLITS.map((option) => {
        const isOff = (option === 'store' && !canSplitByStore) || (option === 'country' && !canSplitByCountry);
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={option === split}
            aria-disabled={isOff}
            className={clsx(styles.chip, option === split && styles.chipOn, isOff && styles.chipDashed)}
            onClick={() => {
              if (isOff) {
                setHint(true);
                return;
              }
              setHint(false);
              onSplit(option);
            }}
          >
            {intl.formatMessage(SPLIT_LABELS[option])}
          </button>
        );
      }),
    [canSplitByCountry, canSplitByStore, intl, onSplit, split]
  );

  return (
    <div className={styles.controls}>
      <select
        className={styles.select}
        aria-label={intl.formatMessage(messages.chartNumber)}
        value={metric}
        onChange={pickMetric}
      >
        {metrics.map((option) => (
          <option key={option} value={option}>
            {intl.formatMessage(streamingTotalLabels[option])}
          </option>
        ))}
      </select>
      <span className={styles.faint}>{intl.formatMessage(messages.splitBy)}</span>
      <div className={styles.chips} role="radiogroup" aria-label={intl.formatMessage(messages.splitBy)}>
        {buttons}
      </div>
      {hint && (!canSplitByStore || !canSplitByCountry) ? (
        <span className={styles.faint} role="status">
          {intl.formatMessage(messages.splitNeedsSource)}
        </span>
      ) : null}
    </div>
  );
};

export const StreamingChartControls = TypedMemo(StreamingChartControlsComponent);
